/*
 * VoxPontAuthPage — standalone sign-in / sign-up for VoxPont.
 *
 * Deliberately self-contained: no HealthDesk AuthPage/AuthFlow imports, no shared
 * form components, no HealthDesk branding. Bespoke navy + gold brand surface with
 * inline design tokens. Google sign-in is implemented inline with the compat SDK.
 *
 * Firebase: compat SDK only (firebaseApp.auth(), firebase.auth.GoogleAuthProvider).
 * Do NOT add modular `firebase/auth` imports here.
 */
import React, { useState } from "react";
import { Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { Controller, useForm } from "react-hook-form";
import { useAuth } from "hooks/useAuth";
import Loading from "components/Loading";
import firebaseApp from "services/firebase";
import firebase from "firebase/compat/app";
import {
  INVITE_ONLY_SIGNUP_MESSAGE,
  isInviteOnlyMode,
  rejectUninvitedOAuthSignup,
} from "components/auth/inviteOnlyGate";
import {
  setVoxPontAuthPreferences,
  productOriginFromParam,
} from "services/auth/userProductOrigin";
import { sanitizeInAppReturnPath } from "features/carestart/utils/sanitizeInAppReturnPath";
import VoxPontLogo from "components/voxpont/VoxPontLogo";
import VoxPontEngineChooser from "components/voxpont/VoxPontEngineChooser";
import {
  persistVoxPontEnginePreference,
  readVoxPontEnginePreference,
  resolveVoxPontPostAuthRoute,
  resolveReleasedVoxPontEngine,
} from "features/voxpontEngine/voxpontEngineRegistry";

function friendlyAuthError(code) {
  switch (code) {
    case "auth/user-not-found":
      return "No account found with that email.";
    case "auth/wrong-password":
    case "auth/invalid-credential":
      return "Incorrect password.";
    case "auth/email-already-in-use":
      return "An account with that email already exists. Try signing in.";
    case "auth/weak-password":
      return "Password must be at least 6 characters.";
    case "auth/invalid-email":
      return "Please enter a valid email address.";
    case "auth/popup-closed-by-user":
      return null; // silent
    default:
      return "Something went wrong. Please try again.";
  }
}

const STYLES = `
.vpauth-root {
  --navy-deep: #0C1E36;
  --navy: #122B4D;
  --royal: #1B4584;
  --gold-brushed: #8c6a1a;
  --gold-antique: #b08a28;
  --gold-polished: #d4af37;
  --gold-champagne: #c8a23a;
  --gold-metal: linear-gradient(105deg,#7a5e17 0%,#8c6a1a 16%,#b08a28 34%,#d4af37 50%,#b08a28 66%,#8c6a1a 84%,#7a5e17 100%);
  min-height: 100vh;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--navy-deep);
  padding: 24px;
  box-sizing: border-box;
  font-family: 'Inter', system-ui, -apple-system, sans-serif;
}
.vpauth-card {
  position: relative;
  width: 100%;
  max-width: 420px;
  background: var(--navy);
  border: 1px solid rgba(196,154,44,0.18);
  border-radius: 10px;
  overflow: hidden;
  box-shadow: 0 20px 60px rgba(0,0,0,0.45);
}
.vpauth-goldbar {
  height: 3px;
  width: 100%;
  background: var(--gold-metal);
}
.vpauth-body { padding: 40px 36px 36px; }
.vpauth-header { text-align: center; margin-bottom: 28px; }
.vpauth-wordmark {
  font-family: 'Federo', serif;
  font-size: 1.8rem;
  line-height: 1.1;
  margin: 0;
  background: var(--gold-metal);
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  color: var(--gold-polished);
}
.vpauth-subline {
  margin: 8px 0 0;
  text-transform: uppercase;
  color: var(--gold-antique);
  font-size: 0.65rem;
  letter-spacing: 0.2em;
}
.vpauth-tagline {
  margin: 10px 0 0;
  color: rgba(255,255,255,0.45);
  font-size: 0.85rem;
}
.vpauth-engine {
  margin-bottom: 24px;
}
.vpauth-engine-label {
  margin: 0 0 8px;
  color: var(--gold-antique);
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}
.vpauth-engine-select {
  cursor: pointer;
}
.vpauth-engine-select:disabled {
  opacity: 0.6;
  cursor: default;
}
.vpauth-engine-select option {
  color: #0C1E36;
  background: #ffffff;
}
.vpauth-engine-helper {
  margin: 8px 0 0;
  color: rgba(255,255,255,0.45);
  font-size: 0.75rem;
  line-height: 1.4;
}
.vpauth-model-detail {
  margin-top: 10px;
}
.vpauth-model-description,
.vpauth-model-includes {
  margin: 0;
  color: rgba(255,255,255,0.55);
  font-size: 0.78rem;
  line-height: 1.45;
}
.vpauth-model-includes {
  margin-top: 6px;
  color: rgba(212, 175, 55, 0.75);
}
.vpauth-banner {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  background: rgba(139,32,32,0.18);
  border: 1px solid rgba(229,120,120,0.4);
  color: #f3c3c3;
  border-radius: 8px;
  padding: 10px 12px;
  font-size: 0.85rem;
  margin-bottom: 20px;
}
.vpauth-banner button {
  margin-left: auto;
  background: none;
  border: none;
  color: inherit;
  font-size: 1rem;
  line-height: 1;
  cursor: pointer;
  padding: 0;
}
.vpauth-success {
  background: rgba(46,125,80,0.18);
  border: 1px solid rgba(129,199,132,0.4);
  color: #bfe3c4;
  border-radius: 8px;
  padding: 10px 12px;
  font-size: 0.85rem;
  margin-bottom: 20px;
  text-align: center;
}
.vpauth-google {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  background: #ffffff;
  color: #1f1f1f;
  border: 1px solid transparent;
  border-radius: 9px;
  padding: 15px 16px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}
.vpauth-google:hover:not(:disabled) {
  border: 1px solid rgba(196,154,44,0.5);
  box-shadow: 0 0 0 3px rgba(196,154,44,0.12);
}
.vpauth-google:disabled { opacity: 0.6; cursor: default; }
.vpauth-google-helper {
  text-align: center;
  color: rgba(255,255,255,0.35);
  font-size: 0.75rem;
  margin: 12px 0 0;
}
.vpauth-divider {
  display: flex;
  align-items: center;
  gap: 14px;
  margin: 24px 0;
  color: rgba(255,255,255,0.3);
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.1em;
}
.vpauth-divider::before,
.vpauth-divider::after {
  content: "";
  flex: 1;
  height: 1px;
  background: rgba(255,255,255,0.12);
}
.vpauth-field { margin-bottom: 16px; }
.vpauth-label {
  display: block;
  color: rgba(255,255,255,0.55);
  font-size: 0.75rem;
  margin-bottom: 6px;
  letter-spacing: 0.02em;
}
.vpauth-input {
  width: 100%;
  box-sizing: border-box;
  background: var(--navy-deep);
  border: 1px solid rgba(255,255,255,0.12);
  border-radius: 8px;
  padding: 12px 14px;
  color: #ffffff;
  font-size: 0.95rem;
  font-family: inherit;
  transition: border-color 0.15s ease;
}
.vpauth-input::placeholder { color: rgba(255,255,255,0.25); }
.vpauth-input:focus {
  outline: none;
  border-color: var(--gold-antique);
}
.vpauth-input.vpauth-input-error { border-color: rgba(229,120,120,0.7); }
.vpauth-field-error {
  color: #e89393;
  font-size: 0.72rem;
  margin: 6px 0 0;
}
.vpauth-forgot {
  display: block;
  text-align: right;
  margin: -4px 0 16px;
  background: none;
  border: none;
  color: var(--gold-antique);
  font-size: 0.75rem;
  cursor: pointer;
  padding: 0;
  margin-left: auto;
}
.vpauth-submit {
  width: 100%;
  background: var(--gold-metal);
  color: var(--navy-deep);
  border: none;
  border-radius: 9px;
  padding: 14px 16px;
  font-size: 1rem;
  font-weight: 700;
  cursor: pointer;
  transition: filter 0.15s ease;
}
.vpauth-submit:hover:not(:disabled) { filter: brightness(1.05); }
.vpauth-submit:disabled { opacity: 0.6; cursor: default; }
.vpauth-toggle {
  text-align: center;
  color: rgba(255,255,255,0.45);
  font-size: 0.85rem;
  margin: 20px 0 0;
}
.vpauth-toggle button {
  background: none;
  border: none;
  color: var(--gold-polished);
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  padding: 0;
}
.vpauth-legal {
  margin: 22px 0 0;
  padding-top: 18px;
  border-top: 1px solid rgba(255,255,255,0.08);
  text-align: center;
  color: rgba(255,255,255,0.4);
  font-size: 0.72rem;
  line-height: 1.55;
}
.vpauth-legal a {
  color: var(--gold-antique);
  text-decoration: none;
}
.vpauth-legal a:hover { text-decoration: underline; }
`;

function GoogleGlyph() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
      <path
        fill="#4285F4"
        d="M17.64 9.205c0-.639-.057-1.252-.164-1.841H9v3.481h4.844a4.14 4.14 0 0 1-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615z"
      />
      <path
        fill="#34A853"
        d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.583-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z"
      />
      <path
        fill="#FBBC05"
        d="M3.964 10.71A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332z"
      />
      <path
        fill="#EA4335"
        d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z"
      />
    </svg>
  );
}

export default function VoxPontAuthPage() {
  const navigate = useNavigate();
  const { user, userData, userLoading } = useAuth();
  const [searchParams] = useSearchParams();
  // Explicit workflow destinations (for example beta invite claims) take precedence
  // for one navigation. Ordinary authentication lands on the chosen released engine.
  const explicitPostAuthRoute = sanitizeInAppReturnPath(searchParams.get("next"));
  // Acquisition channel: `?origin=byoi` stamps the Bring-Your-Own-Institution
  // product origin so these accounts can be tiered/segmented separately.
  const productOrigin = productOriginFromParam(searchParams.get("origin"));
  // Testers arriving from a beta invite link almost never have an account yet,
  // so default the form to "Create account" to avoid a confusing "no account
  // found" on the sign-in path. They can still toggle to "Sign in".
  const arrivingFromInvite = explicitPostAuthRoute?.startsWith("/voxpont-beta-join");
  const inviteOnly = isInviteOnlyMode();
  const [mode, setMode] = useState(
    !inviteOnly && arrivingFromInvite ? "signup" : "signin",
  ); // 'signin' | 'signup'
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [info, setInfo] = useState(null);

  const {
    register,
    control,
    handleSubmit,
    getValues,
    reset,
    formState: { errors },
  } = useForm({
    mode: "onTouched",
    defaultValues: {
      email: "",
      password: "",
      confirmPassword: "",
      engineId: readVoxPontEnginePreference(),
    },
  });

  const isSignup = !inviteOnly && mode === "signup";

  const completeAuthentication = async (authenticatedUser, requestedEngineId) => {
    const engine = resolveReleasedVoxPontEngine(requestedEngineId);
    await setVoxPontAuthPreferences(authenticatedUser.uid, productOrigin, engine.id);
    persistVoxPontEnginePreference(engine.id);
    navigate(resolveVoxPontPostAuthRoute(explicitPostAuthRoute, engine.id));
  };

  const handleGoogleSignIn = async () => {
    setError(null);
    setInfo(null);
    setLoading(true);
    try {
      const provider = new firebase.auth.GoogleAuthProvider();
      const result = await firebaseApp.auth().signInWithPopup(provider);
      if (await rejectUninvitedOAuthSignup(result)) {
        setError(INVITE_ONLY_SIGNUP_MESSAGE);
        return;
      }
      await completeAuthentication(result.user, getValues("engineId"));
    } catch (err) {
      if (err.code !== "auth/popup-closed-by-user") {
        setError(friendlyAuthError(err.code));
      }
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = async (data) => {
    setError(null);
    setInfo(null);
    setLoading(true);
    try {
      if (isSignup) {
        if (isInviteOnlyMode()) {
          setError(INVITE_ONLY_SIGNUP_MESSAGE);
          return;
        }
        const cred = await firebaseApp
          .auth()
          .createUserWithEmailAndPassword(data.email, data.password);
        await completeAuthentication(cred.user, data.engineId);
        setInfo("Account created. Welcome to VoxPont.");
      } else {
        const cred = await firebaseApp
          .auth()
          .signInWithEmailAndPassword(data.email, data.password);
        await completeAuthentication(cred.user, data.engineId);
      }
    } catch (err) {
      setError(friendlyAuthError(err.code));
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async () => {
    setError(null);
    setInfo(null);
    const email = getValues("email");
    if (!email) {
      setError("Enter your email above, then tap “Forgot password?” again.");
      return;
    }
    setLoading(true);
    try {
      await firebaseApp.auth().sendPasswordResetEmail(email);
      setInfo("Password reset email sent. Check your inbox.");
    } catch (err) {
      setError(friendlyAuthError(err.code));
    } finally {
      setLoading(false);
    }
  };

  const switchMode = () => {
    setError(null);
    setInfo(null);
    reset({
      email: "",
      password: "",
      confirmPassword: "",
      engineId: getValues("engineId"),
    });
    setMode(isSignup ? "signin" : "signup");
  };

  if (userLoading) {
    return <Loading page />;
  }
  if (user && !user.isAnonymous && userData === undefined) {
    return <Loading page />;
  }
  if (user && !user.isAnonymous) {
    const savedEngine = resolveReleasedVoxPontEngine(userData?.voxpontEngine);
    return (
      <Navigate
        to={resolveVoxPontPostAuthRoute(explicitPostAuthRoute, savedEngine.id)}
        replace
      />
    );
  }

  return (
    <div className="vpauth-root">
      <style>{STYLES}</style>
      <div className="vpauth-card">
        <div className="vpauth-goldbar" />
        <div className="vpauth-body">
          <div className="vpauth-header">
            <VoxPontLogo
              variant="symbol"
              tone="onDark"
              height={64}
              title="VoxPont"
              style={{ display: "flex", justifyContent: "center", width: "100%", margin: "0 auto 14px" }}
            />
            <h1 className="vpauth-wordmark">VoxPont</h1>
            <p className="vpauth-subline">VOX PONT — Voice across languages</p>
            <p className="vpauth-tagline">
              Secured interpretation in 70+ supported languages · 86 locale choices
            </p>
          </div>

          <div className="vpauth-engine">
            <label className="vpauth-engine-label" htmlFor="vpauth-engine">
              VoxPont Model
            </label>
            <Controller
              name="engineId"
              control={control}
              render={({ field }) => (
                <VoxPontEngineChooser
                  value={field.value}
                  disabled={loading}
                  onChange={(engineId) => {
                    field.onChange(persistVoxPontEnginePreference(engineId));
                  }}
                />
              )}
            />
            <p className="vpauth-engine-helper">
              This model opens after authentication and becomes your account default. VP110–VP430
              are capability configurations on the shared paired platform — not separate apps.
            </p>
          </div>

          {error && (
            <div className="vpauth-banner" role="alert">
              <span>{error}</span>
              <button
                type="button"
                aria-label="Dismiss"
                onClick={() => setError(null)}
              >
                ×
              </button>
            </div>
          )}

          {info && <div className="vpauth-success">{info}</div>}

          <button
            type="button"
            className="vpauth-google"
            onClick={handleGoogleSignIn}
            disabled={loading}
          >
            <GoogleGlyph />
            Continue with Google
          </button>
          <p className="vpauth-google-helper">
            Recommended — sign in faster, no password to remember
          </p>

          <div className="vpauth-divider">or</div>

          <form onSubmit={handleSubmit(onSubmit)} noValidate>
            <div className="vpauth-field">
              <label className="vpauth-label" htmlFor="vpauth-email">
                Email
              </label>
              <input
                id="vpauth-email"
                type="email"
                autoComplete="email"
                placeholder="you@example.com"
                className={`vpauth-input${
                  errors.email ? " vpauth-input-error" : ""
                }`}
                {...register("email", {
                  required: "Please enter your email address.",
                  pattern: {
                    value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                    message: "Please enter a valid email address.",
                  },
                })}
              />
              {errors.email && (
                <p className="vpauth-field-error">{errors.email.message}</p>
              )}
            </div>

            <div className="vpauth-field">
              <label className="vpauth-label" htmlFor="vpauth-password">
                Password
              </label>
              <input
                id="vpauth-password"
                type="password"
                autoComplete={isSignup ? "new-password" : "current-password"}
                placeholder="••••••••"
                className={`vpauth-input${
                  errors.password ? " vpauth-input-error" : ""
                }`}
                {...register("password", {
                  required: "Please enter your password.",
                  ...(isSignup && {
                    minLength: {
                      value: 6,
                      message: "Password must be at least 6 characters.",
                    },
                  }),
                })}
              />
              {errors.password && (
                <p className="vpauth-field-error">{errors.password.message}</p>
              )}
            </div>

            {isSignup && (
              <div className="vpauth-field">
                <label className="vpauth-label" htmlFor="vpauth-confirm">
                  Confirm password
                </label>
                <input
                  id="vpauth-confirm"
                  type="password"
                  autoComplete="new-password"
                  placeholder="••••••••"
                  className={`vpauth-input${
                    errors.confirmPassword ? " vpauth-input-error" : ""
                  }`}
                  {...register("confirmPassword", {
                    required: "Please confirm your password.",
                    validate: (value) =>
                      value === getValues("password") ||
                      "Passwords do not match.",
                  })}
                />
                {errors.confirmPassword && (
                  <p className="vpauth-field-error">
                    {errors.confirmPassword.message}
                  </p>
                )}
              </div>
            )}

            {!isSignup && (
              <button
                type="button"
                className="vpauth-forgot"
                onClick={handleForgotPassword}
                disabled={loading}
              >
                Forgot password?
              </button>
            )}

            <button type="submit" className="vpauth-submit" disabled={loading}>
              {isSignup ? "Create account" : "Sign in"}
            </button>
          </form>

          {inviteOnly ? null : (
          <p className="vpauth-toggle">
            {isSignup ? "Already have an account?" : "New to VoxPont?"}{" "}
            <button type="button" onClick={switchMode} disabled={loading}>
              {isSignup ? "Sign in" : "Create an Account"}
            </button>
          </p>
          )}

          <p className="vpauth-legal">
            By continuing, you agree to VoxPont&rsquo;s{" "}
            <a href="/voxpont-terms" target="_blank" rel="noopener noreferrer">
              Terms of Use
            </a>
            ,{" "}
            <a href="/voxpont-privacy" target="_blank" rel="noopener noreferrer">
              Privacy Policy
            </a>
            , and{" "}
            <a href="/voxpont-baa" target="_blank" rel="noopener noreferrer">
              Business Associate Agreement
            </a>
            .
          </p>
        </div>
      </div>
    </div>
  );
}
